import React, { useState, useEffect, ReactNode, createContext, useContext } from 'react';

// Durumlar için enum
export enum StatusEnum {
    SUCCESS = 'Başarılı',
    INFO = 'Bilgi',
    ERROR = 'Hata'
}

// Gösterim tipi için enum
export enum DisplayType {
    TOP = 'top',
    CENTER = 'center'
}

// Renkler için eşleştirme
const statusColors: { [key in StatusEnum]: string } = {
    [StatusEnum.SUCCESS]: 'green',
    [StatusEnum.INFO]: 'yellow',
    [StatusEnum.ERROR]: 'red'
};

// Bileşen props tanımı
interface StatusMessageProps {
    header: string;
    text: string;
    status: StatusEnum;
    direction?: 'up' | 'down';
    duration?: number;
    displayType?: DisplayType;
    onClose: () => void;
}

const StatusMessage: React.FC<StatusMessageProps> = ({
    header,
    text,
    status,
    direction = 'down',
    duration = 2000,
    displayType = DisplayType.TOP,
    onClose
}) => {
    const [visible, setVisible] = useState(true);
    const [exiting, setExiting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setExiting(true);
            const exitAnimationDuration = 500; // Çıkış animasyonu süresi (ms)
            setTimeout(() => {
                setVisible(false);
                onClose();
            }, exitAnimationDuration);
        }, duration);
        return () => clearTimeout(timer);
    }, [duration, onClose]);

    const containerStyle: React.CSSProperties = {
        backgroundColor: statusColors[status],
        color: 'white',
        padding: '16px',
        position: 'fixed',
        zIndex: 99999,  
        borderRadius: '8px',
        textAlign: 'center',
        transition: 'transform 0.5s ease-out, opacity 0.5s ease-out',
        transform: visible
            ? direction === 'down'
                ? 'translateY(0)'
                : 'translateY(0)'
            : direction === 'down'
                ? 'translateY(-100%)'
                : 'translateY(100%)',
        ...(displayType === DisplayType.CENTER ? { top: '50%', left: '50%', transform: visible ? 'translate(-50%, -50%)' : 'translate(-50%, -150%)', transition: exiting ? 'transform 0.5s ease-out, opacity 0.5s ease-out' : 'none' } : {}),
        ...(displayType === DisplayType.TOP ? { top: '10px', left: '50%', transform: visible ? 'translateX(-50%)' : 'translateX(-50%) translateY(-100%)' } : {}),
        ...(exiting ? { transform: 'translate(-50%, -150%) scale(0.5)', opacity: 0 } : {})
    };

    const backdropStyle: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: displayType === DisplayType.CENTER ? 'block' : 'none'
    };

    return (
        <>
            {displayType === DisplayType.CENTER && <div style={backdropStyle} />}
            <div style={containerStyle}>
                <h3 style={{ fontSize: '18px', color: 'white' }}>{header}</h3>
                <p style={{ fontSize: '14px', color: 'white' }}>{text}</p>
            </div>
        </>
    );
};

// Yardımcı fonksiyon
interface ShowMessageParams {
    header: string;
    text: string;
    status: StatusEnum;
    direction?: 'up' | 'down';
    duration?: number;
    displayType?: DisplayType;
}

const ShowMessageContext = createContext<(params: ShowMessageParams) => void>(() => {});

export const useShowStatusMessage = () => useContext(ShowMessageContext);

// Wrapper bileşen
interface StatusMessageProviderProps {
    children: ReactNode;
}

export const StatusMessageProvider: React.FC<StatusMessageProviderProps> = ({ children }) => {
    const [messages, setMessages] = useState<Array<{ id: number; header: string; text: string; status: StatusEnum; direction?: 'up' | 'down'; duration?: number; displayType?: DisplayType }>>([]);
    const [nextId, setNextId] = useState(1);

    const showMessage = (params: ShowMessageParams) => {
        setMessages((prevMessages) => [
            ...prevMessages,
            { ...params, id: nextId }
        ]);
        setNextId((prevId) => prevId + 1);
    };

    const handleClose = (id: number) => {
        setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== id));
    };

    return (
        <ShowMessageContext.Provider value={showMessage}>
            {children}
            {messages.map((message) => (
                <StatusMessage
                    key={message.id}
                    header={message.header}
                    text={message.text}
                    status={message.status}
                    direction={message.direction}
                    duration={message.duration}
                    displayType={message.displayType}
                    onClose={() => handleClose(message.id)}
                />
            ))}
        </ShowMessageContext.Provider>
    );
};

export default StatusMessage;
